import { css } from 'styled-components';
import { mediaQuery, cssVar } from 'src/app/styles/helper';
import { SpacingPropKeys, RadiusPropKeys } from 'src/app/styles/spacing';

type SpacingDirection = 'Top' | 'Right' | 'Bottom' | 'Left' | '';

type SpacingType = 'margin' | 'padding' | 'gap';

type SpacingBreakpoint = '' | 'Sm' | 'Md' | 'Lg' | 'Xl' | '2xl';

type BoxSizingType = 'border-box' | 'content-box' | 'initial' | 'inherit';

type BorderRadiusDirection = 'TopLeft' | 'TopRight' | 'BottomRight' | 'BottomLeft' | '';

type BorderRadiusMappedProps = {
  [K in `borderRadius${BorderRadiusDirection}${SpacingBreakpoint}`]?: RadiusPropKeys | RadiusPropKeys[];
};

type SpacingMappedProps = {
  [K in `${SpacingType}${SpacingDirection}${SpacingBreakpoint}`]?: SpacingPropKeys | SpacingPropKeys[];
};

type BoxSizingProps = {
  boxSizing?: BoxSizingType;
};

export type SpacingProps = SpacingMappedProps & BoxSizingProps & BorderRadiusMappedProps;

type StyledMappedSpacingProps = {
  [K in keyof SpacingMappedProps as `$${K}`]: SpacingMappedProps[K];
};

type StyledBorderRadiusProps = {
  [K in keyof BorderRadiusMappedProps as `$${K}`]: BorderRadiusMappedProps[K];
};

type StyledBoxSizingProps = {
  [K in keyof BoxSizingProps as `$${K}`]: BoxSizingProps[K];
};

export type StyledSpacingProps = StyledMappedSpacingProps & StyledBoxSizingProps & StyledBorderRadiusProps;

const getSpacingValue = (
  value?: SpacingPropKeys | SpacingPropKeys[] | RadiusPropKeys | RadiusPropKeys[]
): string | undefined => {
  if (!value) return undefined;

  if (Array.isArray(value)) {
    return value.map((v) => cssVar(v)).join(' ');
  }

  return cssVar(value);
};

const getSpacingStyles = (
  type: SpacingType,
  direction: SpacingDirection,
  props: StyledMappedSpacingProps,
  breakpoint: SpacingBreakpoint = ''
): string | undefined => {
  const propName = `$${type}${direction}${breakpoint}` as keyof StyledMappedSpacingProps;
  const value = props[propName];
  if (!value) return undefined;

  const property = direction ? `${type}-${direction.toLowerCase()}` : type;
  return `${property}: ${getSpacingValue(value)};`;
};

const getBorderRadiusStyles = (
  direction: BorderRadiusDirection,
  props: StyledBorderRadiusProps,
  breakpoint: SpacingBreakpoint = ''
): string | undefined => {
  const propName = `$borderRadius${direction}${breakpoint}` as keyof StyledBorderRadiusProps;
  const value = props[propName];
  if (!value) return undefined;

  const property = direction ? `border-${direction.toLowerCase()}-radius` : 'border-radius';
  return `${property}: ${getSpacingValue(value)};`;
};

const getBreakpointStyles = (props: StyledSpacingProps, breakpoint: SpacingBreakpoint) => {
  const styles: string[] = [];
  const types: SpacingType[] = ['margin', 'padding', 'gap'];
  const directions: SpacingDirection[] = ['', 'Top', 'Right', 'Bottom', 'Left'];
  const borderRadiusDirections: BorderRadiusDirection[] = ['', 'TopLeft', 'TopRight', 'BottomRight', 'BottomLeft'];

  types.forEach((type) => {
    directions.forEach((direction) => {
      const style = getSpacingStyles(type, direction, props, breakpoint);
      if (style) styles.push(style);
    });
  });

  borderRadiusDirections.forEach((direction) => {
    const style = getBorderRadiusStyles(direction, props, breakpoint);
    if (style) styles.push(style);
  });

  return styles.length > 0 ? styles.join('\n') : undefined;
};

export const spacingStyles = css<StyledSpacingProps>`
  box-sizing: ${(props) => props.$boxSizing || 'border-box'};

  ${(props) => getBreakpointStyles(props, '')}

  ${(props) => {
    const styles = getBreakpointStyles(props, '2xl');
    if (!styles) return '';

    return mediaQuery(
      '2xl',
      css`
        ${styles}
      `
    );
  }}

  ${(props) => {
    const styles = getBreakpointStyles(props, 'Xl');
    if (!styles) return '';

    return mediaQuery(
      'xl',
      css`
        ${styles}
      `
    );
  }}

  ${(props) => {
    const styles = getBreakpointStyles(props, 'Lg');
    if (!styles) return '';

    return mediaQuery(
      'lg',
      css`
        ${styles}
      `
    );
  }}

  ${(props) => {
    const styles = getBreakpointStyles(props, 'Md');
    if (!styles) return '';

    return mediaQuery(
      'md',
      css`
        ${styles}
      `
    );
  }}

  ${(props) => {
    const styles = getBreakpointStyles(props, 'Sm');
    if (!styles) return '';

    return mediaQuery(
      'sm',
      css`
        ${styles}
      `
    );
  }}
`;
